import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { UserPlus, Search, History, Mail, X, Users, Repeat2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { api, formatApiError } from "../lib/api";

/**
 * Invite players dialog — supports match | tournament | league | lesson.
 * Three pick modes: search registered users, paste emails, or import a past-event roster.
 *
 * Props:
 *   eventKind: "match" | "tournament" | "league" | "lesson"
 *   eventId: string
 *   matchId?: string  (legacy alias for eventId, when eventKind unset → match)
 *   trigger?: ReactNode  (custom trigger)
 *   onInvited?: () => void
 */
export default function InvitePlayersDialog({ eventKind, eventId, matchId, trigger, onInvited }) {
  const kind = eventKind || (matchId ? "match" : "match");
  const id = eventId || matchId;

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("search");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState({});
  const [emails, setEmails] = useState([]);
  const [emailDraft, setEmailDraft] = useState("");
  const [message, setMessage] = useState("");
  const [past, setPast] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || tab !== "search") return;
    const t = setTimeout(() => {
      api.get(`/users/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [q, open, tab]);

  useEffect(() => {
    if (!open || tab !== "past") return;
    api.get("/events/past-roster").then((r) => setPast(r.data)).catch(() => {});
  }, [open, tab]);

  useEffect(() => {
    if (!open || tab !== "attendees") return;
    setAttendeesLoading(true);
    api.get("/events/past-attendees?limit=500")
      .then((r) => setAttendees(r.data.attendees || []))
      .catch(() => setAttendees([]))
      .finally(() => setAttendeesLoading(false));
  }, [open, tab]);

  const toggle = (u) => setPicked((p) => ({ ...p, [u.id]: p[u.id] ? null : u }));
  const removePicked = (uid) => setPicked((p) => ({ ...p, [uid]: null }));
  const addEmail = () => {
    const v = (emailDraft || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { toast.error("Enter a valid email"); return; }
    if (emails.includes(v)) return;
    setEmails((arr) => [...arr, v]);
    setEmailDraft("");
  };
  const removeEmail = (e) => setEmails((arr) => arr.filter((x) => x !== e));

  const pickedUsers = useMemo(
    () => Object.keys(picked).filter((k) => picked[k]).map((k) => picked[k]),
    [picked]
  );

  const importRoster = (ev) => {
    const next = { ...picked };
    let added = 0;
    for (const u of ev.roster) {
      if (!next[u.id]) { next[u.id] = u; added += 1; }
    }
    setPicked(next);
    toast.success(`Imported ${added} player${added === 1 ? "" : "s"} from "${ev.title}"`);
    setTab("search");
  };

  const send = async () => {
    if (pickedUsers.length === 0 && emails.length === 0) {
      toast.error("Pick at least one player or add an email");
      return;
    }
    setBusy(true);
    try {
      // Use unified events route. The /events/{kind}/{id}/invite endpoint also sends email.
      const url = `/events/${kind}/${id}/invite`;
      const payload = {
        user_ids: pickedUsers.map((u) => u.id),
        emails,
        message,
      };
      const { data } = await api.post(url, payload);
      const total = (data.invited_users?.length || 0) + (data.invited_emails?.length || 0);
      toast.success(`Invited ${total} player${total === 1 ? "" : "s"}`);
      setOpen(false);
      setPicked({}); setEmails([]); setMessage(""); setEmailDraft(""); setQ("");
      onInvited?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const TabBtn = ({ k, icon, label }) => (
    <button type="button" onClick={() => setTab(k)} data-testid={`invite-tab-${k}`}
      className={`pp-chip flex items-center gap-1 ${tab === k ? "pp-chip-active" : ""}`}>
      {icon} {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button data-testid="invite-trigger" className="pp-btn-secondary px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
            <UserPlus size={14} /> Invite
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-[#131313] border-white/10 text-white max-w-lg" data-testid="invite-dialog">
        <DialogHeader><DialogTitle className="font-heading">Invite players</DialogTitle></DialogHeader>

        <div className="flex gap-2 flex-wrap">
          <TabBtn k="search" icon={<Search size={12} />} label="Search" />
          <TabBtn k="attendees" icon={<Repeat2 size={12} />} label="Past attendees" />
          <TabBtn k="past" icon={<History size={12} />} label="From past event" />
          <TabBtn k="email" icon={<Mail size={12} />} label="Email" />
        </div>

        {tab === "search" && (
          <>
            <div className="relative">
              <Search size={14} className="absolute start-3 top-3 text-zinc-500" />
              <input data-testid="invite-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="pp-input ps-9" />
            </div>
            <div className="max-h-56 overflow-auto space-y-1" data-testid="invite-results">
              {results.map((u) => (
                <button key={u.id} type="button" onClick={() => toggle(u)} data-testid={`invite-pick-${u.id}`}
                  className={`w-full flex items-center gap-2 p-2 rounded-lg ${picked[u.id] ? "bg-[#00E676]/10 border border-[#00E676]/40" : "hover:bg-white/5"}`}>
                  <Avatar className="h-8 w-8"><AvatarImage src={u.avatar_url || ""} /><AvatarFallback className="bg-[#007AFF] text-white text-xs">{u.first_name?.[0] || "?"}</AvatarFallback></Avatar>
                  <div className="text-start text-sm flex-1">
                    <div>{u.first_name} {u.last_name}</div>
                    <div className="text-xs text-zinc-500">{u.email} · {u.role}</div>
                  </div>
                  {picked[u.id] ? <span className="text-[#00E676] text-xs">✓</span> : null}
                </button>
              ))}
              {results.length === 0 && <div className="text-zinc-500 text-sm p-2">Type to search.</div>}
            </div>
          </>
        )}

        {tab === "attendees" && (
          <div className="space-y-2" data-testid="invite-attendees">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-zinc-400 flex items-center gap-1">
                <Users size={12} /> {attendeesLoading ? "Loading…" : `${attendees.length} unique past attendee${attendees.length === 1 ? "" : "s"}`}
              </div>
              {attendees.length > 0 && (
                <div className="flex gap-1">
                  <button type="button" data-testid="invite-attendees-select-all"
                    onClick={() => setPicked((p) => {
                      const next = { ...p };
                      let added = 0;
                      for (const a of attendees) {
                        if (!next[a.user_id]) {
                          next[a.user_id] = { id: a.user_id, first_name: a.first_name, last_name: a.last_name, email: a.email, avatar_url: a.avatar_url };
                          added += 1;
                        }
                      }
                      if (added > 0) toast.success(`Added ${added} player${added === 1 ? "" : "s"}`);
                      return next;
                    })}
                    className="pp-btn-primary px-2.5 py-1 rounded-lg text-xs">
                    Select all
                  </button>
                  <button type="button" data-testid="invite-attendees-clear"
                    onClick={() => {
                      setPicked((p) => {
                        const next = { ...p };
                        for (const a of attendees) delete next[a.user_id];
                        return next;
                      });
                    }}
                    className="pp-chip text-xs">
                    Clear
                  </button>
                </div>
              )}
            </div>
            <p className="text-[11px] text-zinc-500">
              One-click re-engagement: people who joined your previous events, sorted by loyalty. Great for promoting a new tournament/league to your community.
            </p>
            <div className="max-h-72 overflow-auto space-y-1">
              {!attendeesLoading && attendees.length === 0 && (
                <div className="text-zinc-500 text-sm p-3 text-center">No past attendees yet — once your events complete, they'll appear here.</div>
              )}
              {attendees.map((a) => {
                const isPicked = !!picked[a.user_id];
                return (
                  <button
                    key={a.user_id}
                    type="button"
                    onClick={() => toggle({ id: a.user_id, first_name: a.first_name, last_name: a.last_name, email: a.email, avatar_url: a.avatar_url })}
                    data-testid={`invite-attendee-${a.user_id}`}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg ${isPicked ? "bg-[#00E676]/10 border border-[#00E676]/40" : "hover:bg-white/5"}`}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={a.avatar_url || ""} />
                      <AvatarFallback className="bg-[#007AFF] text-white text-xs">{a.first_name?.[0] || "?"}</AvatarFallback>
                    </Avatar>
                    <div className="text-start text-sm flex-1 min-w-0">
                      <div className="truncate">{a.first_name} {a.last_name}</div>
                      <div className="text-xs text-zinc-500 flex items-center gap-1 flex-wrap">
                        <span>{a.event_count} event{a.event_count === 1 ? "" : "s"}</span>
                        {a.last_attended_at && <span>· last {new Date(a.last_attended_at).toLocaleDateString()}</span>}
                        {(a.kinds || []).slice(0, 3).map((k) => (
                          <span key={k} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 uppercase">{k}</span>
                        ))}
                      </div>
                    </div>
                    {isPicked ? <span className="text-[#00E676] text-xs">✓</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {tab === "past" && (
          <div className="max-h-72 overflow-auto space-y-2" data-testid="invite-past">
            {past.length === 0 && <div className="text-zinc-500 text-sm p-2">No past events with players yet.</div>}
            {past.map((ev) => (
              <div key={`${ev.kind}-${ev.id}`} className="pp-card p-3" data-testid={`invite-past-${ev.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate">{ev.title}</div>
                    <div className="text-xs text-zinc-500 capitalize">{ev.kind} · {ev.when ? new Date(ev.when).toLocaleDateString() : "—"} · {ev.roster.length} player(s)</div>
                  </div>
                  <button type="button" onClick={() => importRoster(ev)} data-testid={`invite-past-import-${ev.id}`}
                    className="pp-btn-primary px-3 py-1.5 rounded-lg text-xs whitespace-nowrap">Import roster</button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {ev.roster.slice(0, 6).map((u) => (
                    <span key={u.id} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5">{u.first_name} {u.last_name?.[0] || ""}</span>
                  ))}
                  {ev.roster.length > 6 && <span className="text-[11px] text-zinc-500">+{ev.roster.length - 6} more</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "email" && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <input data-testid="invite-email-draft" value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                placeholder="player@example.com" className="pp-input flex-1" type="email" />
              <button type="button" onClick={addEmail} data-testid="invite-email-add" className="pp-btn-secondary px-3 py-2 rounded-lg text-sm">Add</button>
            </div>
            <div className="flex flex-wrap gap-1" data-testid="invite-emails-chips">
              {emails.map((em) => (
                <span key={em} className="text-xs pp-chip flex items-center gap-1">{em}
                  <button type="button" onClick={() => removeEmail(em)} aria-label="Remove" className="text-zinc-500 hover:text-[#FF3B30]"><X size={10} /></button>
                </span>
              ))}
              {emails.length === 0 && <span className="text-xs text-zinc-500">No emails added yet.</span>}
            </div>
            <p className="text-xs text-zinc-500">External emails get a Resend invite with a join QR/link — they don't need an account to RSVP.</p>
          </div>
        )}

        {/* Selected pile */}
        {pickedUsers.length > 0 && (
          <div className="mt-2" data-testid="invite-selected">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Selected players ({pickedUsers.length})</div>
            <div className="flex flex-wrap gap-1">
              {pickedUsers.map((u) => (
                <span key={u.id} className="text-xs pp-chip flex items-center gap-1">{u.first_name} {u.last_name}
                  <button type="button" onClick={() => removePicked(u.id)} aria-label="Remove" className="text-zinc-500 hover:text-[#FF3B30]"><X size={10} /></button>
                </span>
              ))}
            </div>
          </div>
        )}

        <textarea data-testid="invite-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
          placeholder="Optional message..." className="pp-input" />
        <button onClick={send} disabled={busy} data-testid="invite-send"
          className="pp-btn-primary px-4 py-2 rounded-lg">{busy ? "Sending…" : "Send invites"}</button>
      </DialogContent>
    </Dialog>
  );
}
